import { useEffect, useState } from "react";
import axiosInstance from "../components/api/axiosInstance";

/**
 * Which portal this page is served on (resolved once per page load):
 * - staff:  office.tumma.co (Tumma staff)
 * - shared: crm.digixleads.com (tenants without their own domain)
 * - tenant: office.<domain> of one tenant ({ tenant_id, name })
 * - dev:    localhost / LAN IP (not locked)
 * - unknown
 */
let resolvePromise = null;
let resolved = null;

export function currentPortalHost() {
  return typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
}

export function resolvePortalHost() {
  if (resolved) return Promise.resolve(resolved);
  if (!resolvePromise) {
    resolvePromise = axiosInstance
      .get("/portal/resolve", { params: { host: currentPortalHost() } })
      .then((res) => {
        resolved = res.data?.data || { kind: "unknown", host: currentPortalHost() };
        return resolved;
      })
      .catch(() => {
        resolvePromise = null;
        return { kind: "unknown", host: currentPortalHost(), failed: true };
      });
  }
  return resolvePromise;
}

export function usePortalHost() {
  const [portal, setPortal] = useState(resolved);
  useEffect(() => {
    let active = true;
    if (!resolved) {
      resolvePortalHost().then((data) => {
        if (active) setPortal(data);
      });
    }
    return () => {
      active = false;
    };
  }, []);
  return portal;
}

/** Tenant email domain implied by an office.<domain> host, e.g. office.acme.com -> acme.com. */
export function domainFromPortalHost(host) {
  const h = String(host || "").toLowerCase();
  return h.startsWith("office.") ? h.slice("office.".length) : "";
}

const NOTICE_KEY = "portal_notice";

/** Keep sign-in warnings (wrong host / login email) for the in-app banner. */
export function savePortalNotice(data) {
  try {
    const notice = {
      hostMismatch: !!data?.portal?.host_mismatch,
      expectedHost: data?.portal?.expected_host || null,
      url: data?.portal?.url || null,
      emailIssue: data?.login_email_mismatch ? data?.login_email_issue || "Login email needs updating." : null,
    };
    if (notice.hostMismatch || notice.emailIssue) {
      sessionStorage.setItem(NOTICE_KEY, JSON.stringify(notice));
    } else {
      sessionStorage.removeItem(NOTICE_KEY);
    }
  } catch (_) {}
}

export function readPortalNotice() {
  try {
    const raw = sessionStorage.getItem(NOTICE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

export function dismissPortalNotice() {
  try {
    sessionStorage.removeItem(NOTICE_KEY);
  } catch (_) {}
}
